/**
 * ADP-60 — action-level bid-change impact read model (before-7d vs after-7d).
 *
 * Pure read-model DTO: aggregated from bid_change_events + target_daily_metrics
 * at query time, never persisted. `delta` is only non-null when dataStatus is
 * "ready" so the API never implies a verdict from incomplete windows.
 */
import { z } from "zod";

const isoDateTimeSchema = z.string().datetime({ offset: true });
const uuidSchema = z.string().uuid();
const calendarDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const nullableMoneySchema = z.number().finite().nonnegative().nullable();

export const ActionImpactDataStatusSchema = z.enum([
  "ready",
  "waiting_for_after_window",
  "insufficient_metrics",
]);
export type ActionImpactDataStatus = z.infer<typeof ActionImpactDataStatusSchema>;

export const ActionImpactReasonSchema = z.enum([
  "target_missing",
  "after_window_incomplete",
  "no_before_metrics",
  "no_after_metrics",
]);
export type ActionImpactReason = z.infer<typeof ActionImpactReasonSchema>;

export const ActionImpactMetricsSchema = z
  .object({
    impressions: z.number().int().nonnegative(),
    clicks: z.number().int().nonnegative(),
    spend: z.number().finite().nonnegative(),
    sales: z.number().finite().nonnegative(),
    orders: z.number().int().nonnegative(),
    acos: z.number().finite().nonnegative().nullable(),
    roas: z.number().finite().nonnegative().nullable(),
    cvr: z.number().finite().nonnegative().nullable(),
    cpc: z.number().finite().nonnegative().nullable(),
  })
  .superRefine((metrics, context) => {
    if (metrics.clicks === 0 && metrics.cvr !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "CVR must be null when clicks are zero",
        path: ["cvr"],
      });
    }
  });
export type ActionImpactMetrics = z.infer<typeof ActionImpactMetricsSchema>;

export const ActionImpactWindowSchema = z.object({
  startDate: calendarDateSchema,
  endDate: calendarDateSchema,
  daysCovered: z.number().int().nonnegative(),
  metrics: ActionImpactMetricsSchema,
});
export type ActionImpactWindow = z.infer<typeof ActionImpactWindowSchema>;

/** after − before; ratio deltas are null when either side is null. */
export const ActionImpactDeltaSchema = z.object({
  impressions: z.number().int(),
  clicks: z.number().int(),
  spend: z.number().finite(),
  sales: z.number().finite(),
  orders: z.number().int(),
  acos: z.number().finite().nullable(),
  roas: z.number().finite().nullable(),
  cvr: z.number().finite().nullable(),
  cpc: z.number().finite().nullable(),
});
export type ActionImpactDelta = z.infer<typeof ActionImpactDeltaSchema>;

export const ActionImpactStatusDetailSchema = z.object({
  expectedDays: z.number().int().positive(),
  afterWindowEndDate: calendarDateSchema,
  reasons: z.array(ActionImpactReasonSchema),
});
export type ActionImpactStatusDetail = z.infer<typeof ActionImpactStatusDetailSchema>;

export const ActionImpactV1Schema = z.object({
  schemaVersion: z.literal(1),
  aiActionId: uuidSchema,
  bidChangeEventId: uuidSchema,
  targetId: uuidSchema.nullable(),
  campaignId: uuidSchema,
  actionType: z.string().min(1),
  currencyCode: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .nullable(),
  executedAt: isoDateTimeSchema,
  oldBid: nullableMoneySchema,
  newBid: nullableMoneySchema,
  changePercentage: z.number().finite().nullable(),
  before: ActionImpactWindowSchema,
  after: ActionImpactWindowSchema,
  delta: ActionImpactDeltaSchema.nullable(),
  dataStatus: ActionImpactDataStatusSchema,
  statusDetail: ActionImpactStatusDetailSchema,
});
export type ActionImpactV1 = z.infer<typeof ActionImpactV1Schema>;
